import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async (attempt = 0) => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setReady(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Genuinely not authenticated — clear session.
        setUser(false);
        setReady(true);
      } else if (attempt < 4) {
        // Transient failure (server busy / 5xx / timeout / network). DO NOT log the
        // user out — keep their session and retry shortly. This prevents users from
        // being kicked to the login screen when the backend is momentarily slow.
        setReady(true);
        setTimeout(() => refresh(attempt + 1), 2000 * (attempt + 1));
      } else {
        // Give up retrying, but never downgrade an existing session to logged-out
        // because of a transient error — only treat as logged-out on first load.
        setUser((prev) => (prev ? prev : false));
        setReady(true);
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, register, logout, refresh }),
    [user, ready, login, register, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
